// src/redux/slices/hampersSlice.js
import { createSlice } from '@reduxjs/toolkit';

// Dummy product data (you can replace these with real API data later)
const dummyProducts = [
  {
    id: 1,
    title: 'Cool Hoodie',
    type: 'hoodie',
    sizes: ['M', 'L', 'XL'],
    colors: ['blue', 'red'],
    fabric: 'Cotton',
    printMethod: 'Screen Printing',
    printPlacement: 'Front',
    price: 50,
    rating: 4,
    description: 'A cool hoodie with a comfortable fit and modern design.',
  },
  {
    id: 2,
    title: 'Sporty Sweatshirt',
    type: 'sweatshirt',
    sizes: ['S', 'M', 'L'],
    colors: ['black', 'green'],
    fabric: 'Polyester',
    printMethod: 'DTG',
    printPlacement: 'Back',
    price: 40,
    rating: 3,
    description: 'A sporty sweatshirt perfect for workouts.',
  },
  {
    id: 3,
    title: 'Classic Hoodie',
    type: 'hoodie',
    sizes: ['XS', 'S', 'M', 'L'],
    colors: ['red', 'yellow'],
    fabric: 'Cotton-Poly Blend',
    printMethod: 'Embroidery',
    printPlacement: 'Sleeve',
    price: 60,
    rating: 5,
    description: 'A classic hoodie with a vintage vibe.',
  },
  {
    id: 4,
    title: 'Urban Sweatshirt',
    type: 'sweatshirt',
    sizes: ['M', 'L', 'XL', '2XL'],
    colors: ['purple', 'black'],
    fabric: 'Cotton',
    printMethod: 'Heat Transfer',
    printPlacement: 'Hood',
    price: 55,
    rating: 4,
    description: 'An urban sweatshirt with a trendy design.',
  },
];

// Default filter criteria
const initialFilters = {
  personalize: false,
  productTypes: { hoodies: false, sweatshirts: false },
  sizes: { XS: false, S: false, M: false, L: false, XL: false, '2XL': false, '3XL': false },
  colors: { black: false, green: false, yellow: false, red: false, purple: false, custom: false },
  fabric: { cotton: false, polyester: false, cottonPolyBlend: false },
  printMethod: { screenPrinting: false, dtg: false, dtf: false, embroidery: false, heatTransfer: false },
  printPlacement: { front: false, back: false, sleeve: false, hood: false },
  customization: { oneColor: false, fullColor: false, logoOnly: false },
  orientation: '',
  corners: '',
  designStyle: { inter: false, prata: false, protestRevolution: false, promo: false },
  priceRange: 100,
  rates: { five: false, four: false, three: false },
};

const initialState = {
  products: dummyProducts,
  filters: initialFilters,
  filteredProducts: dummyProducts,
};

/**
 * Helper function to filter products based on the current filters.
 * In this demo, filtering is applied to:
 * - Product type (mapping 'hoodie' → 'hoodies', otherwise 'sweatshirts')
 * - Sizes (if any selected)
 * - Price (filters out products above the priceRange)
 * - Rating (if any rating is selected)
 */
function filterProducts(products, filters) {
  // Get selected product types (e.g. hoodies, sweatshirts)
  const selectedTypes = Object.entries(filters.productTypes)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  // Get selected sizes
  const selectedSizes = Object.entries(filters.sizes)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  // Get selected rating values (convert keys to numbers)
  const selectedRates = Object.entries(filters.rates)
    .filter(([_, value]) => value)
    .map(([key]) => {
      if (key === 'five') return 5;
      if (key === 'four') return 4;
      if (key === 'three') return 3;
      return null;
    })
    .filter((rate) => rate !== null);

  return products.filter((product) => {
    // Filter by product type if any selected
    if (selectedTypes.length > 0) {
      const prodTypeKey = product.type === 'hoodie' ? 'hoodies' : 'sweatshirts';
      if (!selectedTypes.includes(prodTypeKey)) return false;
    }
    // Filter by sizes if any selected
    if (selectedSizes.length > 0) {
      if (!product.sizes.some((size) => selectedSizes.includes(size))) return false;
    }
    // Filter by price range
    if (product.price > filters.priceRange) return false;
    // Filter by rating if any selected
    if (selectedRates.length > 0) {
      if (!selectedRates.includes(product.rating)) return false;
    }
    return true;
  });
}

const hampersSlice = createSlice({
  name: 'hampers',
  initialState,
  reducers: {
    // For simple filters (e.g. "personalize")
    setSimpleFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // For nested filters (e.g. productTypes, sizes, colors, etc.)
    setNestedFilter: (state, action) => {
      const { category, key, value } = action.payload;
      state.filters[category][key] = value;
    },
    // For radio button filters (e.g. orientation or corners)
    setRadioFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // For updating the price range from a range input
    setPriceRange: (state, action) => {
      state.filters.priceRange = action.payload;
    },
    // Apply current filters to compute the filteredProducts array
    applyFilters: (state) => {
      state.filteredProducts = filterProducts(state.products, state.filters);
    },
    // Reset filters to their default values
    resetFilters: (state) => {
      state.filters = { ...initialFilters };
      state.filteredProducts = state.products;
    },
  },
});

export const {
  setSimpleFilter,
  setNestedFilter,
  setRadioFilter,
  setPriceRange,
  applyFilters,
  resetFilters,
} = hampersSlice.actions;

export default hampersSlice.reducer;
